import type { ReactNode } from "react";
import { Apple, Globe } from "lucide-react";
import { cn } from "../lib/utils";
import { STAGGER_AFTER_DETAIL_BLOCKS_STEP, staggerMs } from "../lib/stagger";
import type { AppDistributionChannel } from "../data/site";

/** Monochrome play-triangle (paired with “Google Play” label). */
function GooglePlayIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={cn("h-4 w-4 shrink-0", className)}
      aria-hidden
    >
      <path d="M8 5v14l11-7-11-7z" />
    </svg>
  );
}

const linkClass =
  "inline-flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-4 py-2.5 text-sm font-medium text-foreground backdrop-blur-sm transition-all hover:border-primary/40 hover:bg-secondary/70 hover:shadow-[0_0_16px_-4px] hover:shadow-primary/20";
const disabledClass =
  "inline-flex items-center gap-2 rounded-lg border border-border/70 bg-secondary/20 px-4 py-2.5 text-sm font-medium text-muted-foreground/90 backdrop-blur-sm";

type Props = {
  appStore?: AppDistributionChannel;
  playStore?: AppDistributionChannel;
  web?: AppDistributionChannel;
  appStoreUrl?: string;
  playStoreUrl?: string;
  webUrl?: string;
  className?: string;
  staggerStartMs?: number;
};

export default function AppDistributionLinks({
  appStore,
  playStore,
  web,
  appStoreUrl,
  playStoreUrl,
  webUrl,
  className,
  staggerStartMs = staggerMs(STAGGER_AFTER_DETAIL_BLOCKS_STEP),
}: Props) {
  const resolvedAppStore = appStore ?? (appStoreUrl ? { href: appStoreUrl } : undefined);
  const resolvedPlayStore = playStore ?? (playStoreUrl ? { href: playStoreUrl } : undefined);
  const resolvedWeb = web ?? (webUrl ? { href: webUrl } : undefined);

  const items = [
    resolvedAppStore && (resolvedAppStore.href || resolvedAppStore.statusLabel)
      ? {
          key: "app-store",
          href: resolvedAppStore.href,
          label: resolvedAppStore.label ?? "App Store",
          statusLabel: resolvedAppStore.statusLabel,
          icon: <Apple className="h-4 w-4 shrink-0" aria-hidden />,
        }
      : null,
    resolvedPlayStore && (resolvedPlayStore.href || resolvedPlayStore.statusLabel)
      ? {
          key: "play-store",
          href: resolvedPlayStore.href,
          label: resolvedPlayStore.label ?? "Google Play",
          statusLabel: resolvedPlayStore.statusLabel,
          icon: <GooglePlayIcon />,
        }
      : null,
    resolvedWeb && (resolvedWeb.href || resolvedWeb.statusLabel)
      ? {
          key: "web",
          href: resolvedWeb.href,
          label: resolvedWeb.label ?? "Web",
          statusLabel: resolvedWeb.statusLabel,
          icon: <Globe className="h-4 w-4 shrink-0" aria-hidden />,
        }
      : null,
  ].filter(Boolean) as {
    key: string;
    href?: string;
    label: string;
    statusLabel?: string;
    icon: ReactNode;
  }[];

  if (items.length === 0) return null;

  return (
    <div
      className={cn("stagger-in flex flex-wrap gap-3", className)}
      style={{ animationDelay: `${staggerStartMs}ms` }}
    >
      {items.map((item) => (
        item.href ? (
          <a
            key={item.key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={linkClass}
          >
            {item.icon}
            <span>{item.label}</span>
          </a>
        ) : (
          <div key={item.key} aria-disabled="true" className={disabledClass}>
            {item.icon}
            <span>{item.label}</span>
            {item.statusLabel ? (
              <span className="rounded-full border border-border bg-background/70 px-2 py-0.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                {item.statusLabel}
              </span>
            ) : null}
          </div>
        )
      ))}
    </div>
  );
}
